'use client'

import { useEffect } from 'react'

import useSessionStore, { SESSION_STORE_KEY } from '@/stores/useSessionStore'

// Cross-tab sync: update in-memory session state when another tab (or another
// app on the same origin, e.g. cruises-next) writes to the session-store
// localStorage key. Mounted as a component so the listener uses a live store
// reference and re-attaches cleanly across HMR / remounts.
const SessionStoreSync = (): null => {
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== SESSION_STORE_KEY) return

      let nextState: {
        authToken?: string | null
        userId?: string | null
        userEmail?: string | null
        userRoles?: string[]
        mobileVerified?: boolean
        termsAccepted?: boolean
        hasGuestUserSaved?: boolean
      } | null = null
      try {
        const parsed = e.newValue ? JSON.parse(e.newValue) : null
        nextState = parsed?.state ?? null
      } catch {
        return
      }

      const { setAuth, clearSession, setHasGuestUserSaved } = useSessionStore.getState()
      const nextAuthToken = nextState?.authToken ?? null

      if (!nextAuthToken) {
        clearSession()
        return
      }

      setAuth(
        nextAuthToken,
        nextState?.userId ?? null,
        nextState?.userEmail ?? null,
        nextState?.userRoles ?? [],
        nextState?.mobileVerified ?? false,
        nextState?.termsAccepted ?? false,
      )
      setHasGuestUserSaved(nextState?.hasGuestUserSaved ?? false)
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return null
}

export default SessionStoreSync
